import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from "react";
import { getBonusRules } from "../api/getBonusRules";
import { useBetSlip } from "./BetSlipProvider";

interface BonusContextType {
  rawBonuses: IBetSlipBonus[];
  isLoading: boolean;
  error: Error | null;
  refetchBonuses: () => void;
}

const BonusContext = createContext<BonusContextType | undefined>(undefined);

interface BonusProviderProps {
  children: ReactNode;
}

const fetchBonusRules = async (
  token: string,
  channelId: string,
  brandId: string,
): Promise<IBetSlipBonus[]> => {
  const response = await getBonusRules({
    userToken: token,
    channelId,
    brandId,
  });
  return response.data || [];
};

export const BonusProvider: React.FC<BonusProviderProps> = ({ children }) => {
  const { config } = useBetSlip();
  const [rawBonuses, setRawBonuses] = useState<IBetSlipBonus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchBonuses = useCallback(async () => {
    if (!config.user.token || !config.channelId || !config.brand.id) return;
    setIsLoading(true);
    setError(null);
    try {
      setRawBonuses(
        await fetchBonusRules(
          config.user.token,
          config.channelId,
          config.brand.id,
        ),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch bonuses"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [config.user.token, config.channelId, config.brand.id]);

  useEffect(() => {
    fetchBonuses();
  }, [fetchBonuses]);

  const contextValue = useMemo<BonusContextType>(
    () => ({ rawBonuses, isLoading, error, refetchBonuses: fetchBonuses }),
    [rawBonuses, isLoading, error, fetchBonuses],
  );

  return (
    <BonusContext.Provider value={contextValue}>
      {children}
    </BonusContext.Provider>
  );
};

export const useBonuses = () => {
  const context = useContext(BonusContext);
  if (!context) {
    throw new Error("useBonuses must be used within a BonusProvider");
  }
  return context;
};
